Namespace('Monitoring.LiveReports.RealtimeMap')

class Monitoring.LiveReports.RealtimeMapView extends Backbone.View
  template: JST["backbone/templates/monitoring/live_reports/realtime_map/realtime_map"]
  className: 'live-report-container'

  initialize: (@options = {}) ->
    @accountId      = @options.account_id
    @filterDefinition = @options.filter_definition
    @layersEndpointUrl = @options.layers_endpoint_url
    @drilldownEndpointUrl = @options.drilldown_endpoint_url
    @categoryToIconMapping = @options.category_to_icon_mapping
    @centerPoint    = @options.map.center_point
    @initialZoom    = @options.map.initial_zoom
    @minZoom        = @options.map.min_zoom
    @southWestBound = [@options.map.bounds.bottom, @options.map.bounds.left]
    @northEastBound = [@options.map.bounds.top, @options.map.bounds.right]
    @heatmapState   = 'off'
    @refreshPeriod = 30000

  render: ->
    @$el.html(@template(@))

  initializeMap: (containerElementOrId) ->
    @map = Leaflet.map(containerElementOrId, @mapOptions())
    # TODO: render the base map while the layers are being fetched
    @addZoomControl()
    @addButtons()
    @refresh() # This fetches the layers from the server
    @addEventBindings()

  addEventBindings: ->
    @map.on('zoomend', @waitAndRefresh)

  waitAndRefresh: =>
    clearTimeout(@zoomTimeout) if @zoomTimeout
    @zoomTimeout = setTimeout =>
      @refresh()
    , 1000

  addZoomControl: ->
    Leaflet.control.zoom({ position: 'bottomright' }).addTo(@map)

  addOverlays: (geoCountsByCategories) ->
    @categoryOverlays = @categoriesAsOverlays(geoCountsByCategories)
    # This is an object with category names as keys and layerGroups as values

    @heatmapOverlay = @heatmapLayerGroup(geoCountsByCategories)

    @removeAllCategories()

    # the CoffeeScript equivalent of Hash#keys
    @categoryNames = (categoryName for own categoryName, layerGroup of @categoryOverlays)

    # the CoffeeScript equivalent of Hash#values
    @categoryLayerGroups = (layerGroup for own categoryName, layerGroup of @categoryOverlays)

    # we want all the categories to be visible at first
    @initializeVisibilities() unless @layerVisibilities

    if @heatmapState == 'on'
      @turnHeatmapOn()
    else
      @addAllSelectedCategories()

    @trigger('added-layers', @categoryNames, @layerVisibilities) unless @sidebarHasLayers
    @sidebarHasLayers = true

    # the layerGroups will change their z-index every 3 seconds
    @zIndices = _.map(@categoryLayerGroups, (layerGroup, i) => i) unless @zIndices
    clearTimeout(@rotationTimeout) if @rotationTimeout
    @rotationTimeout = setInterval =>
      @rotate()
    , 3000

  initializeVisibilities: ->
    @layerVisibilities = {}
    _.each(@categoryNames, (categoryName) => @layerVisibilities[categoryName] = true)

  removeAllCategories: ->
    _.each((@categoryLayerGroups || []), (layerGroup) => @map.removeLayer(layerGroup))

  addAllSelectedCategories: ->
    for own categoryName, layerGroup of @categoryOverlays
      layerGroup.addTo(@map) if @layerVisibilities[categoryName]

  addLayer: (layerName) ->
    @layerVisibilities[layerName] = true
    @map.addLayer(@categoryOverlays[layerName])

  removeLayer: (layerName) ->
    @layerVisibilities[layerName] = false
    @map.removeLayer(@categoryOverlays[layerName])

  toggleHeatmap: ->
    if @heatmapState == 'on'
      @turnHeatmapOff()
    else
      @turnHeatmapOn()

  turnHeatmapOff: ->
    @removeAllLayers()
    @addAllSelectedCategories()
    @heatmapState = 'off'

  turnHeatmapOn: ->
    @removeAllLayers()
    @map.addLayer(@heatmapOverlay)
    @heatmapState = 'on'

  removeAllLayers: ->
    @map.eachLayer((layer) =>
      @map.removeLayer(layer) unless layer instanceof Leaflet.TileLayer
    )

  rotate: ->
    _.each(@categoryLayerGroups, (layerGroup, i) =>
      layerGroup.eachLayer((layer) =>
        layer.setZIndexOffset(@zIndices[i])
      )
    )
    @zIndices.push(@zIndices.shift()) # this rotates the array

  addButtons: ->
    Leaflet.easyButton({
      states: [
        {
          stateName: 'closed-sidebar'
          icon: 'fa-bars'
          onClick: (control) =>
            @trigger('click-filter')
            control.state('opened-sidebar')
        },
        {
          stateName: 'opened-sidebar'
          icon: 'fa-angle-left fa-bold fa-2x'
          onClick: (control) =>
            @trigger('click-filter')
            control.state('closed-sidebar')
        }
      ]
    }).addTo(@map)

  heatmapLayerGroup: (geoCountsByCategories) ->
    Leaflet.heatLayer(@arrayOfPointsWithIntensities(geoCountsByCategories), {radius: 25})

  mapOptions: ->
    {
      center: @centerPoint
      zoom: @initialZoom
      layers: @layers()
      minZoom: @minZoom
      maxBounds: @maxBounds()
      zoomControl: false
      attributionControl: false
    }

  layers: ->
    @baseLayers()

  baseLayers: ->
    [@osmLayer()]

  osmLayer: ->
    osmUrl    = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    osmAttrib = 'Map data © <a href="https://openstreetmap.org">OpenStreetMap</a> contributors';
    new Leaflet.tileLayer(osmUrl, {minZoom: 2, attribution: osmAttrib})

  maxBounds: ->
    Leaflet.latLngBounds([@southWestBound, @northEastBound])

  categoriesAsOverlays: (geoCountsByCategories) ->
    _.reduce(geoCountsByCategories, (overlays, category) =>
      resultsAsMarkers = _.map(category.results, (result) =>
        marker = Leaflet.marker([result.lat, result.lon], @markerOptions(category, result))
        marker.on('click', @onClickCategory)
        marker
      )
      countMarkers = _.map(category.results, (result) =>
        Leaflet.marker([result.lat, result.lon], @countMarkerOptions(category, result))
      )
      resultAndCountMarkers = _.union(resultsAsMarkers, countMarkers)
      overlays[category.name] = Leaflet.layerGroup(resultAndCountMarkers)
      overlays
    , {})

  #TODO rename
  arrayOfPointsWithIntensities: (geoCountsByCategories) ->
    _.flatten(_.map(geoCountsByCategories, (category) =>
      _.map(category.results, (result) =>
        [result.lat, result.lon, result.count]
      )
    ), true)

  markerOptions: (category, result) ->
    categoryName = category.name
    resultCount  = result.count
    {
      categoryName: categoryName
      icon: @getIconFromCategoryName(categoryName)
      title: "#{categoryName}: #{resultCount}"
      riseOnHover: true
      result: result
    }

  countMarkerOptions: (category, result) ->
    categoryName = category.name
    resultCount  = result.count
    {
      icon: @getIconFromCount(resultCount)
      title: "#{categoryName}: #{resultCount}"
    }

  getIconFromCategoryName: (categoryName) ->
    iconFilename = @categoryNameToIcon(categoryName)
    Leaflet.icon({
      iconUrl: window.image_path(iconFilename)
      iconSize: [25, 25]
    })

  getIconFromCount: (count) ->
    Leaflet.divIcon({
      className: 'category-count-icon'
      html: "<span>#{count}</span>"
    })

  categoryNameToIcon: (categoryName) ->
    ret = @categoryToIconMapping[categoryName.trim().toLowerCase().replace(' ', '_').replace('>', '-').replace('á', 'a').replace('é', 'e').replace('í', 'i').replace('ó', 'o').replace('ú', 'u')]
    if ret then ret else 'peligro.svg'

  setFilter: (newFilterDefinition) ->
    @filterDefinition = newFilterDefinition

  refresh: ->
    clearTimeout(@refreshTimeout) if @refreshTimeout

    filterDefinition = $.extend({ account: @accountId }, @getMapBoundsCondition(), @filterDefinition)
    console.log('realtime map filter:')
    console.log(filterDefinition)
    $.ajax
      type: 'POST'
      url: @layersEndpointUrl
      global: false
      data: { filter_definition: filterDefinition, precision: @appropriatePrecision() }
      success: (response) =>
        if response.success
          @addOverlays(JSON.parse(response.result))
          @refreshTimeout = setTimeout =>
            @refresh()
          , @refreshPeriod
          @trigger('ready')
        else
          console.log('An error has occurred trying to get map options')
      error: (response) =>
        console.log('An error has occurred trying to get map options')

  getMapBoundsCondition: ->
    bounds = @maxBounds()
    sw = bounds.getSouthWest()
    ne = bounds.getNorthEast()
    top_left = [ne.lat, Math.max(sw.lng, -179.99)] # Elasticsearch fails if longitude is -180 o 180 :S
    bottom_right = [sw.lat, Math.min(ne.lng, 179.99)]

    geolocation: # Unfortunately this name coincides with the name of a field...
      field: 'geolocation'
      top_left: top_left
      bottom_right: bottom_right

  # Map zoom (1-20) to geo hash grid precision (1-12)
  appropriatePrecision: ->
    switch @map.getZoom()
      when 0, 1, 2    then 2
      when 3, 4       then 3
      when 5, 6, 7, 8 then 4
      when 9, 10, 11  then 5
      when 12, 13     then 6
      when 14, 15     then 7
      when 16         then 8
      when 17         then 9
      when 18         then 10
      else 11

  onClickCategory: (evt) =>
    drilldownData = {
      categoryName: evt.target.options.categoryName
      result: evt.target.options.result
    }
    # this has south_west, north_east, and more irrelevant data (for now)
    @drilldown(drilldownData)

  drilldown: (drilldownData) ->
    filterDefinition = $.extend({ account: @accountId }, @filterDefinition, { category_name: drilldownData.categoryName }, @getDrilldownBoundsCondition(drilldownData.result))
    @trigger('drilldown', { endpointUrl: @drilldownEndpointUrl, filterDefinition: filterDefinition, accountId: @accountId } )

  getDrilldownBoundsCondition: (result) ->
    # TODO: check if the precision types condition is reaching this point
    geolocation: # Unfortunately this name coincides with the name of a field...
      field: 'geolocation'
      top_left: [result.north_east[0], result.south_west[1]]
      bottom_right: [result.south_west[0], result.north_east[1]]
